import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Info, Save, ShieldAlert, Sparkles } from 'lucide-react';
import { apiFetch } from '../../../../shared/apiClient';
import { JiraFieldPicker } from '../../../../shared/components/JiraFieldPicker';
import { CustomSelect } from '../../../../shared/components/CustomSelect';
import type { QualityOverride, QualityOverrideTargets } from '../qualityApi';
import { QualityApi } from '../qualityApi';
import './QualitySettingsPanel.css';

interface QualityTargets {
  newBugsMax: number;
  firstTimePassMin: number;
  reopenRateMax: number;
  bugStoryRatioMax: number;
  investmentBugRatioMax: number;
}

interface QualitySettings {
  failure_statuses: string[];
  qa_statuses: string[];
  dev_statuses: string[];
  /** Terminal Jira statuses counted as delivered (Dynamics orange Done line). */
  done_statuses: string[];
  bug_types: string[];
  bug_type_field_id: string;
  escape_root_cause_field_id: string;
  team_estimates_bugs: boolean;
  /** Minutes in a failure status before First-time pass counts it (default 15). */
  first_time_pass_failure_min_dwell_minutes: number;
  targets: QualityTargets;
}

interface Options {
  statuses: string[];
  issueTypes: string[];
  customFields: Array<{
    id: number;
    jiraFieldId: string;
    displayName: string;
    fieldKind: string;
  }>;
}

interface ProductInfo {
  id: string;
  name: string;
}

async function fetchSettings(): Promise<{ settings: QualitySettings; options: Options }> {
  const res = await apiFetch('/api/quality/settings');
  if (!res.ok) throw new Error(`Failed to load quality settings: ${res.statusText}`);
  return res.json();
}

async function saveSettings(patch: QualitySettings): Promise<QualitySettings> {
  const res = await apiFetch('/api/quality/settings', {
    method: 'PUT',
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Failed to save quality settings: ${res.statusText}`);
  const data = (await res.json()) as { settings: QualitySettings };
  return data.settings;
}

const HINTS = {
  bugTypes:
    'Jira issue types your team treats as a bug (Bug, Defect, Incident). Used by New bugs and Bug-to-Story ratio.',
  qa: 'Statuses your team uses for QA work (e.g. To Verify, In Testing, Verified). A story that enters any of them counts as “reached QA”. Also powers the QA debt line on Dynamics.',
  failure:
    'Statuses your team uses when QA finds a problem (e.g. Verification Failed). A visit here is the “failure” signal for First-time pass.',
  failureDwell:
    'First-time pass counts a failure only if the issue stays in a Failure status this long. Shorter visits are ignored as accidental clicks. Set to 0 to count every visit.',
  dev: 'Statuses your team uses for dev work. Going back from Done or Verified to any of them counts as a reopen. Also powers the Dev debt line on Dynamics.',
  doneTerminal:
    'Jira statuses that mean an item is finished for reporting (e.g. Done, Closed, Resolved). These map to the orange Done line on Dynamics · Plan vs Done — the legend label stays “Done”.',
  targets: 'Numbers your team aims for. They decide when a KPI card turns green, amber, or red.',
  optional:
    'Optional Jira fields and team toggles. They unlock extra cards: By Bug Type, Escape taxonomy, and bug-aware Sprint predictability.',
  bugType:
    'Pick the custom field that splits bugs into types (e.g. “Bug Type” with values like “Bug (new feature)” or “Bug (production)”). Powers the By Bug Type card.',
  escapeCause:
    'Pick the custom field that says why a bug escaped to production (regression, missed requirement, environment, data, flaky). Powers the Escape taxonomy chart and root-cause table.',
};

/** Order of the steps shown in the panel. Mirrors KpiHintPopover anchorIds. */
const STEP_IDS = [
  'quality-step-1',
  'quality-step-2',
  'quality-step-3',
  'quality-step-4',
  'quality-step-5',
  'quality-step-6',
] as const;

export const QualitySettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<QualitySettings | null>(null);
  const [options, setOptions] = useState<Options | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [highlightedStep, setHighlightedStep] = useState<string | null>(null);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Product selector for KPI targets (Step 5) ──
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductInfo[]>([]);
  const [overrides, setOverrides] = useState<QualityOverride[]>([]);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetError, setTargetError] = useState<string | null>(null);
  const [targetSaved, setTargetSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [{ settings, options }, overridesRes, productsRes] = await Promise.all([
          fetchSettings(),
          QualityApi.getQualityOverrides(),
          apiFetch('/api/products'),
        ]);
        if (cancelled) return;
        setSettings(settings);
        setOptions(options);
        setOverrides(overridesRes.overrides);
        if (productsRes.ok) {
          const payload = await productsRes.json();
          setProducts(
            (payload.products ?? []).map((p: ProductInfo) => ({ id: p.id, name: p.name }))
          );
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load settings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Deep-link handler: when the URL carries `?section=quality-step-N`, scroll
   * the matching block into view and pulse it. Removes the param after use so
   * a refresh doesn't re-trigger the highlight.
   */
  useEffect(() => {
    if (loading) return;
    const section = searchParams.get('section');
    if (!section) return;
    if (!STEP_IDS.includes(section as (typeof STEP_IDS)[number])) return;

    let cancelled = false;
    let clear: number | undefined;

    const run = (): boolean => {
      const el = stepRefs.current[section];
      if (!el || cancelled) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlightedStep(section);
      const next = new URLSearchParams(searchParams);
      next.delete('section');
      setSearchParams(next, { replace: true });
      clear = window.setTimeout(() => {
        if (!cancelled) setHighlightedStep((s) => (s === section ? null : s));
      }, 4500);
      return true;
    };

    const t = window.setTimeout(() => {
      if (run()) return;
      requestAnimationFrame(() => {
        if (!cancelled) run();
      });
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (clear) window.clearTimeout(clear);
    };
  }, [loading, searchParams, setSearchParams]);

  const update = (patch: Partial<QualitySettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  /**
   * Push a freshly-registered catalog entry into the local `options` so that
   * BOTH pickers on this panel (Bug Type + Escape root-cause) see it as
   * "Registered" without requiring a page reload — even when the entry was
   * created via the OTHER picker on the same page.
   */
  const upsertRegisteredOption = (entry: {
    id: number;
    jiraFieldId: string;
    displayName: string;
    fieldKind: string;
  }) => {
    setOptions((prev) => {
      if (!prev) return prev;
      if (prev.customFields.some((f) => f.jiraFieldId === entry.jiraFieldId)) return prev;
      return {
        ...prev,
        customFields: [...prev.customFields, entry].sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        ),
      };
    });
  };

  const updateTarget = (patch: Partial<QualityTargets>) => {
    setSettings((prev) => (prev ? { ...prev, targets: { ...prev.targets, ...patch } } : prev));
  };

  // ── Product selector helpers ──
  const isOverride = useMemo(
    () => !!selectedProductId && overrides.some((o) => o.productId === selectedProductId),
    [selectedProductId, overrides]
  );

  const displayedTargets = useMemo(() => {
    if (!selectedProductId) return settings?.targets;
    const o = overrides.find((ov) => ov.productId === selectedProductId);
    return o?.targets ?? settings?.targets;
  }, [selectedProductId, overrides, settings?.targets]);

  const switchProduct = useCallback((productId: string | null) => {
    setSelectedProductId(productId);
    setTargetError(null);
    setTargetSaved(false);
    if (!productId) return;
  }, []);

  const updateDisplayTarget = useCallback(
    (patch: Partial<QualityTargets>) => {
      if (!settings) return;
      if (!selectedProductId) {
        // Editing org default — update the main settings.targets
        updateTarget(patch);
      } else {
        // Editing a product override — update the local overrides state
        setOverrides((prev) => {
          const idx = prev.findIndex((o) => o.productId === selectedProductId);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = { ...updated[idx], targets: { ...updated[idx].targets, ...patch } };
            return updated;
          }
          // No override yet — create a draft entry
          const product = products.find((p) => p.id === selectedProductId);
          return [
            ...prev,
            {
              productId: selectedProductId,
              productName: product?.name ?? selectedProductId,
              targets: { ...settings.targets, ...patch },
            },
          ];
        });
      }
    },
    [selectedProductId, settings, products]
  );

  const handleSaveTargets = useCallback(async () => {
    if (!settings) return;
    setTargetSaving(true);
    setTargetError(null);
    setTargetSaved(false);
    try {
      if (!selectedProductId) {
        const updated = await saveSettings(settings);
        setSettings(updated);
      } else {
        const o = overrides.find((ov) => ov.productId === selectedProductId);
        const targetsToSave: QualityOverrideTargets = o?.targets ?? {
          newBugsMax: settings.targets.newBugsMax,
          firstTimePassMin: settings.targets.firstTimePassMin,
          reopenRateMax: settings.targets.reopenRateMax,
          bugStoryRatioMax: settings.targets.bugStoryRatioMax,
          investmentBugRatioMax: settings.targets.investmentBugRatioMax,
        };
        await QualityApi.saveQualityOverride(selectedProductId, targetsToSave);
        const updated = await QualityApi.getQualityOverrides();
        setOverrides(updated.overrides);
      }
      setTargetSaved(true);
      setTimeout(() => setTargetSaved(false), 3000);
    } catch (err) {
      setTargetError(err instanceof Error ? err.message : 'Failed to save targets');
    } finally {
      setTargetSaving(false);
    }
  }, [selectedProductId, settings, overrides]);

  const handleResetOverride = useCallback(async () => {
    if (!selectedProductId) return;
    setTargetSaving(true);
    setTargetError(null);
    try {
      await QualityApi.deleteQualityOverride(selectedProductId);
      const updated = await QualityApi.getQualityOverrides();
      setOverrides(updated.overrides);
      setTargetSaved(true);
      setTimeout(() => setTargetSaved(false), 3000);
    } catch (err) {
      setTargetError(err instanceof Error ? err.message : 'Failed to reset override');
    } finally {
      setTargetSaving(false);
    }
  }, [selectedProductId]);

  const toggleFromList = (key: keyof QualitySettings, value: string) => {
    if (!settings) return;
    const arr = settings[key];
    if (!Array.isArray(arr)) return;
    const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
    update({ [key]: next } as Partial<QualitySettings>);
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await saveSettings(settings);
      setSettings(updated);
      setSuccess('Quality settings saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading quality settings…</p>;
  if (!settings || !options) {
    return <p className="quality-settings-error">{error ?? 'Failed to load settings.'}</p>;
  }

  /** Helper to register a step's DOM ref by id. */
  const setStepRef = (id: string) => (el: HTMLDivElement | null) => {
    stepRefs.current[id] = el;
  };

  /** Returns the className with highlight class added when this step is targeted. */
  const stepClass = (id: string, base: string) =>
    `${base} quality-step${highlightedStep === id ? ' quality-step--highlight' : ''}`;

  /** Numbered badge shown at the start of every step heading. */
  const StepNumber: React.FC<{ n: number; done?: boolean }> = ({ n, done }) => (
    <span className={`quality-step-number${done ? ' quality-step-number--done' : ''}`} aria-hidden>
      {n}
    </span>
  );

  /** A status considered configured if at least one item is picked. */
  const isFilled = (a: string[]) => a.length > 0;

  return (
    <div className="quality-settings-panel" id="quality-settings">
      <header className="quality-settings-panel__header">
        <h2>
          <ShieldAlert size={20} />
          Quality settings
        </h2>
        <p>
          Tell the Quality tab which Jira statuses and issue types your team uses. Follow the steps
          below — every KPI card on the Quality tab links back here. These settings are shared
          across the whole organization.
        </p>
      </header>

      <section
        className="quality-settings-workflow-summary"
        aria-label="Workflow statuses used by Quality analytics"
      >
        <h3 className="quality-settings-workflow-summary__title">Workflow statuses in use</h3>
        <p className="quality-settings-field__note quality-settings-workflow-summary__intro">
          <strong>Dynamics · Plan vs Done:</strong> <strong>Plan</strong> counts every issue in the
          period bucket (any status — not filtered by these lists). Orange <strong>Done</strong> ⇢
          Terminal (Done); green <strong>In Dev</strong> ⇢ Dev; cyan <strong>In QA</strong> ⇢ QA;
          purple <strong>Failed checks</strong> ⇢ transitions into Failure.
        </p>
        <WorkflowStatusRow label="Dev statuses" values={settings.dev_statuses} variant="dev" />
        <WorkflowStatusRow label="QA statuses" values={settings.qa_statuses} variant="qa" />
        <WorkflowStatusRow
          label="Failure statuses"
          values={settings.failure_statuses}
          variant="failure"
        />
        <WorkflowStatusRow
          label="Terminal (Done) statuses"
          values={settings.done_statuses}
          variant="done"
        />
      </section>

      {/* ---------- Step 1: Bug-like issue types ---------- */}
      <div
        ref={setStepRef('quality-step-1')}
        id="quality-step-1"
        className={stepClass('quality-step-1', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber n={1} done={isFilled(settings.bug_types)} />
          <strong>Bug-like issue types</strong>
          <HintIcon hint={HINTS.bugTypes} />
        </div>
        <p className="quality-settings-field__note">
          Powers <em>New bugs</em> and the top number of <em>Bug-to-Story ratio</em>.
        </p>
        <Chips
          picked={settings.bug_types}
          options={options.issueTypes}
          onToggle={(v) => toggleFromList('bug_types', v)}
        />
      </div>

      {/* ---------- Step 2: QA statuses ---------- */}
      <div
        ref={setStepRef('quality-step-2')}
        id="quality-step-2"
        className={stepClass('quality-step-2', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber n={2} done={isFilled(settings.qa_statuses)} />
          <strong>QA statuses</strong>
          <HintIcon hint={HINTS.qa} />
        </div>
        <p className="quality-settings-field__note">
          Bottom number of <em>First-time pass</em>. A story is counted the moment it enters any of
          these (e.g. To Verify, In Testing, Verified).
        </p>
        <Chips
          picked={settings.qa_statuses}
          options={options.statuses}
          onToggle={(v) => toggleFromList('qa_statuses', v)}
        />
      </div>

      {/* ---------- Step 3: Failure statuses (+ dwell) ---------- */}
      <div
        ref={setStepRef('quality-step-3')}
        id="quality-step-3"
        className={stepClass('quality-step-3', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber n={3} done={isFilled(settings.failure_statuses)} />
          <strong>Failure statuses</strong>
          <HintIcon hint={HINTS.failure} />
        </div>
        <p className="quality-settings-field__note">
          Top number of <em>First-time pass</em>. A story is marked as failed if it ever made a real
          visit to one of these (e.g. Verification Failed).
        </p>
        <Chips
          picked={settings.failure_statuses}
          options={options.statuses}
          onToggle={(v) => toggleFromList('failure_statuses', v)}
        />

        <div className="quality-settings-subfield">
          <div className="quality-settings-field__label quality-settings-field__label--sub">
            <strong>Minimum time in failure status</strong>
            <HintIcon hint={HINTS.failureDwell} />
          </div>
          <div className="quality-settings-targets">
            <TargetInput
              label="Stay in failure status for at least"
              value={settings.first_time_pass_failure_min_dwell_minutes}
              onChange={(n) =>
                update({
                  first_time_pass_failure_min_dwell_minutes: Number.isFinite(n)
                    ? Math.max(0, n)
                    : 15,
                })
              }
              step={1}
              suffix="minutes"
            />
          </div>
          <p className="quality-settings-field__note">
            Default <strong>15 min</strong>. Shorter visits are ignored as mis-clicks. Set to 0 to
            count every visit.
          </p>
        </div>
      </div>

      {/* ---------- Step 4: Dev statuses ---------- */}
      <div
        ref={setStepRef('quality-step-4')}
        id="quality-step-4"
        className={stepClass('quality-step-4', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber
            n={4}
            done={isFilled(settings.dev_statuses) && isFilled(settings.done_statuses)}
          />
          <strong>Dev statuses</strong>
          <HintIcon hint={HINTS.dev} />
        </div>
        <p className="quality-settings-field__note">
          Going back from Done or Verified to any of these counts as a reopen — powers the{' '}
          <em>Reopen rate</em> card.
        </p>
        <Chips
          picked={settings.dev_statuses}
          options={options.statuses}
          onToggle={(v) => toggleFromList('dev_statuses', v)}
        />

        <div className="quality-settings-subfield">
          <div className="quality-settings-field__label quality-settings-field__label--sub">
            <strong>Terminal (Done) statuses</strong>
            <HintIcon hint={HINTS.doneTerminal} />
          </div>
          <p className="quality-settings-field__note">
            Orange <em>Done</em> line on <em>Dynamics · Plan vs Done</em> counts issues whose
            current Jira status is one of these.
          </p>
          <Chips
            picked={settings.done_statuses}
            options={options.statuses}
            onToggle={(v) => toggleFromList('done_statuses', v)}
          />
        </div>
      </div>

      {/* ---------- Step 5: KPI targets ---------- */}
      <div
        ref={setStepRef('quality-step-5')}
        id="quality-step-5"
        className={stepClass('quality-step-5', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber n={5} done />
          <strong>KPI targets</strong>
          <HintIcon hint={HINTS.targets} />
        </div>
        <p className="quality-settings-field__note">
          Numbers that decide when a KPI card turns green, amber, or red.
        </p>

        {/* Product selector */}
        <div className="quality-settings-target-selector">
          <label className="quality-settings-target-selector__label">
            <span>Product</span>
            <CustomSelect
              value={selectedProductId ?? ''}
              options={[
                { value: '', label: 'Organization default' },
                ...products.map((p) => ({
                  value: p.id,
                  label: `${p.name}${overrides.some((o) => o.productId === p.id) ? ' *' : ''}`,
                })),
              ]}
              onChange={(val) => switchProduct(val || null)}
              style={{ minWidth: 200, fontSize: '13px' }}
            />
          </label>
          {selectedProductId && !isOverride && (
            <p className="quality-settings-field__note">
              Using org defaults. Edit and save to create a product-specific override.
            </p>
          )}
          {isOverride && (
            <p className="quality-settings-field__note">
              Product-specific override active.{' '}
              <span style={{ color: 'var(--color-text-muted)' }}>
                Org defaults: bugs ≤ {settings.targets.newBugsMax}, ratio ≤{' '}
                {settings.targets.bugStoryRatioMax}, pass ≥ {settings.targets.firstTimePassMin}%
              </span>
            </p>
          )}
        </div>

        {displayedTargets && (
          <div className="quality-settings-targets">
            <TargetInput
              label="New bugs (max)"
              value={displayedTargets.newBugsMax}
              onChange={(n) => updateDisplayTarget({ newBugsMax: n })}
              step={1}
              suffix="per period"
            />
            <TargetInput
              label="Bug-to-Story (max)"
              value={displayedTargets.bugStoryRatioMax}
              onChange={(n) => updateDisplayTarget({ bugStoryRatioMax: n })}
              step={0.05}
            />
            <TargetInput
              label="First-time pass (min)"
              value={displayedTargets.firstTimePassMin}
              onChange={(n) => updateDisplayTarget({ firstTimePassMin: n })}
              step={1}
              suffix="%"
            />
            <TargetInput
              label="Reopen rate (max)"
              value={displayedTargets.reopenRateMax}
              onChange={(n) => updateDisplayTarget({ reopenRateMax: n })}
              step={1}
              suffix="%"
            />
            <TargetInput
              label="Bug investment ratio (max)"
              value={displayedTargets.investmentBugRatioMax}
              onChange={(n) => updateDisplayTarget({ investmentBugRatioMax: n })}
              step={0.05}
            />
          </div>
        )}

        {targetError && <p className="quality-settings-error">{targetError}</p>}

        <div className="quality-settings-target-actions">
          {isOverride && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleResetOverride}
              disabled={targetSaving}
            >
              Reset to org default
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleSaveTargets}
            disabled={targetSaving}
          >
            <Save size={14} />
            {targetSaving ? 'Saving…' : 'Save targets'}
          </button>
          {targetSaved && (
            <span className="quality-settings-success" style={{ fontSize: '0.82rem' }}>
              Saved
            </span>
          )}
        </div>
      </div>

      {/* ---------- Step 6: Optional fields ---------- */}
      <div
        ref={setStepRef('quality-step-6')}
        id="quality-step-6"
        className={stepClass('quality-step-6', 'quality-settings-field')}
      >
        <div className="quality-settings-field__label">
          <StepNumber n={6} />
          <strong>Optional — unlock extra cards</strong>
          <span className="quality-step-tag">
            <Sparkles size={11} aria-hidden /> optional
          </span>
          <HintIcon hint={HINTS.optional} />
        </div>

        <div className="quality-settings-subfield">
          <div className="quality-settings-field__label quality-settings-field__label--sub">
            <strong>Bug Type classifier field</strong>
            {!settings.bug_type_field_id && <MissingBadge label="Missing: Bug Type" />}
            <HintIcon hint={HINTS.bugType} />
          </div>
          <JiraFieldPicker
            value={settings.bug_type_field_id}
            onChange={(v) => update({ bug_type_field_id: v })}
            registeredFields={options.customFields}
            allowAdd
            placeholder="Select a Jira field to classify bugs…"
            clearOptionLabel="— None (turns off the By Bug Type card) —"
            description={
              <>
                Search by name or id. Don’t see it? Pick it under <em>Other Jira fields</em> — we
                will add it to the catalog and select it for you.
              </>
            }
            onRegistered={upsertRegisteredOption}
          />
        </div>

        <div className="quality-settings-subfield">
          <div className="quality-settings-field__label quality-settings-field__label--sub">
            <strong>Escape root-cause field</strong>
            {!settings.escape_root_cause_field_id && <MissingBadge label="Missing: Escape cause" />}
            <HintIcon hint={HINTS.escapeCause} />
          </div>
          <JiraFieldPicker
            value={settings.escape_root_cause_field_id}
            onChange={(v) => update({ escape_root_cause_field_id: v })}
            registeredFields={options.customFields}
            allowAdd
            placeholder="Select a Jira field with root-cause values…"
            clearOptionLabel="— None (turns off the K · Escape taxonomy card) —"
            description={
              <>
                Pick the Jira field that says <em>why</em> a bug escaped to production (regression,
                missed requirement, environment, data, flaky).
              </>
            }
            onRegistered={upsertRegisteredOption}
          />
        </div>

        <div className="quality-settings-subfield">
          <div className="quality-settings-field__label quality-settings-field__label--sub">
            <strong>Team estimation habits</strong>
          </div>
          <label
            className="quality-settings-checkbox"
            title="Leave off if your team estimates only stories."
          >
            <input
              type="checkbox"
              checked={settings.team_estimates_bugs}
              onChange={(e) => update({ team_estimates_bugs: e.target.checked })}
            />
            <span>
              Our team estimates bugs (affects <em>I · Sprint predictability</em>)
            </span>
          </label>
          <p className="quality-settings-field__note">
            When off, the card shows a small note saying your team only estimates stories — so an
            empty bugs section won’t look like a missing sync.
          </p>
        </div>
      </div>

      <footer className="quality-settings-panel__footer">
        {error && <p className="quality-settings-error">{error}</p>}
        {success && <p className="quality-settings-success">{success}</p>}
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={16} />
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </footer>
    </div>
  );
};

const HintIcon: React.FC<{ hint: string }> = ({ hint }) => (
  <span className="stat-hint-icon" title={hint} aria-label={hint}>
    <Info size={14} />
  </span>
);

const MissingBadge: React.FC<{ label: string }> = ({ label }) => (
  <span
    className="quality-settings-missing-badge"
    title="This field is not configured — the matching card in Analytics → Quality will stay empty."
  >
    <AlertTriangle size={10} />
    {label}
  </span>
);

const WorkflowStatusRow: React.FC<{
  label: string;
  values: string[];
  variant: 'dev' | 'qa' | 'failure' | 'done';
}> = ({ label, values, variant }) => (
  <div className={`quality-settings-workflow-row quality-settings-workflow-row--${variant}`}>
    <span className="quality-settings-workflow-row__label">{label}</span>
    {values.length === 0 ? (
      <span className="quality-settings-workflow-row__empty">None selected</span>
    ) : (
      <ul className="quality-settings-workflow-row__list" aria-label={label}>
        {values.map((v) => (
          <li key={v} className="quality-settings-workflow-row__item">
            {v}
          </li>
        ))}
      </ul>
    )}
  </div>
);

interface ChipsProps {
  picked: string[];
  options: string[];
  onToggle: (value: string) => void;
}

const Chips: React.FC<ChipsProps> = ({ picked, options, onToggle }) => {
  const [custom, setCustom] = useState('');
  const add = () => {
    const v = custom.trim();
    if (v && !picked.includes(v)) onToggle(v);
    setCustom('');
  };
  return (
    <>
      <div className="quality-settings-chips">
        {picked.map((v) => (
          <button
            key={v}
            type="button"
            className="quality-settings-chip quality-settings-chip--on"
            onClick={() => onToggle(v)}
            title="Remove"
          >
            {v} ×
          </button>
        ))}
        {options
          .filter((o) => !picked.includes(o))
          .map((o) => (
            <button
              key={o}
              type="button"
              className="quality-settings-chip"
              onClick={() => onToggle(o)}
              title="Add"
            >
              + {o}
            </button>
          ))}
      </div>
      <div className="quality-settings-add">
        <input
          type="text"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Add a custom value…"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className="btn btn-secondary btn-sm" onClick={add}>
          Add
        </button>
      </div>
    </>
  );
};

interface TargetInputProps {
  label: string;
  value: number;
  onChange: (n: number) => void;
  step: number;
  suffix?: string;
}

const TargetInput: React.FC<TargetInputProps> = ({ label, value, onChange, step, suffix }) => (
  <label className="quality-settings-target">
    <span className="quality-settings-target__label">{label}</span>
    <div className="quality-settings-target__input">
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        step={step}
        min={0}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
      />
      {suffix && <span className="quality-settings-target__suffix">{suffix}</span>}
    </div>
  </label>
);

export default QualitySettingsPanel;
